import { useSubscription } from "@/hooks/useSubscription";
import { useMemo } from "react";

export interface FeatureLimits {
  maxCustomers: number;
  maxStampsPerMonth: number;
  maxReferralsPerMonth: number;
  hasAnalytics: boolean;
  hasAdvancedCRM: boolean;
  hasLandingPages: boolean;
  hasWalletIntegration: boolean;
  hasCustomBranding: boolean;
  hasPrioritySupport: boolean;
}

export interface TenantFeaturePermission {
  id: string;
  tenant_id: string;
  feature_key: string;
  is_enabled: boolean;
  enabled_by_user_id?: string;
  enabled_at?: string;
  created_at: string;
  updated_at: string;
}

// Define plan features based on subscription tiers
const PLAN_FEATURES: Record<string, FeatureLimits> = {
  'prod_T3VskPPSh6LORX': { // Basic Plan
    maxCustomers: 100,
    maxStampsPerMonth: 500,
    maxReferralsPerMonth: 50,
    hasAnalytics: true,
    hasAdvancedCRM: false,
    hasLandingPages: false,
    hasWalletIntegration: false,
    hasCustomBranding: false,
    hasPrioritySupport: false,
  },
  'prod_T3VsE4Lz3QjIbs': { // Pro Plan
    maxCustomers: 500,
    maxStampsPerMonth: 2000,
    maxReferralsPerMonth: 200,
    hasAnalytics: true,
    hasAdvancedCRM: true,
    hasLandingPages: true,
    hasWalletIntegration: true,
    hasCustomBranding: false,
    hasPrioritySupport: false,
  },
  'prod_T3VsSjdXmcJ74K': { // Enterprise Plan
    maxCustomers: Infinity,
    maxStampsPerMonth: Infinity,
    maxReferralsPerMonth: Infinity,
    hasAnalytics: true,
    hasAdvancedCRM: true,
    hasLandingPages: true,
    hasWalletIntegration: true,
    hasCustomBranding: true,
    hasPrioritySupport: true,
  },
};

// Free plan limits
const FREE_PLAN_FEATURES: FeatureLimits = {
  maxCustomers: 25,
  maxStampsPerMonth: 100,
  maxReferralsPerMonth: 10,
  hasAnalytics: false,
  hasAdvancedCRM: false,
  hasLandingPages: false,
  hasWalletIntegration: false,
  hasCustomBranding: false,
  hasPrioritySupport: false,
};

export function useSubscriptionAccess() {
  const { subscriptionStatus, getCurrentPlan, plans } = useSubscription();

  const currentFeatures = useMemo((): FeatureLimits => {
    if (!subscriptionStatus.subscribed || !subscriptionStatus.product_id) {
      return FREE_PLAN_FEATURES;
    }

    return PLAN_FEATURES[subscriptionStatus.product_id] || FREE_PLAN_FEATURES;
  }, [subscriptionStatus.subscribed, subscriptionStatus.product_id]);

  const isFeatureAvailable = (feature: keyof FeatureLimits): boolean => {
    const featureValue = currentFeatures[feature];
    if (typeof featureValue === 'boolean') {
      return featureValue;
    }
    return featureValue > 0;
  };

  const canAddCustomers = (currentCount: number): boolean => {
    return currentCount < currentFeatures.maxCustomers;
  };

  const canAddStamps = (currentMonthStamps: number): boolean => {
    return currentMonthStamps < currentFeatures.maxStampsPerMonth;
  };

  const canAddReferrals = (currentMonthReferrals: number): boolean => {
    return currentMonthReferrals < currentFeatures.maxReferralsPerMonth;
  };

  const getRemainingCustomers = (currentCount: number): number => {
    return Math.max(0, currentFeatures.maxCustomers - currentCount);
  };

  const getRemainingStamps = (currentMonthStamps: number): number => {
    return Math.max(0, currentFeatures.maxStampsPerMonth - currentMonthStamps);
  };

  const getRemainingReferrals = (currentMonthReferrals: number): number => {
    return Math.max(0, currentFeatures.maxReferralsPerMonth - currentMonthReferrals);
  };

  const getUpgradeMessage = (feature: keyof FeatureLimits): string => {
    const currentPlan = getCurrentPlan();
    const currentPlanName = currentPlan?.name || "Free Plan";

    switch (feature) {
      case 'hasAnalytics':
        return `Analytics sind ab dem Basic Plan verfügbar. Sie nutzen aktuell: ${currentPlanName}`;
      case 'hasAdvancedCRM':
        return `Erweiterte CRM-Funktionen sind ab dem Pro Plan verfügbar. Sie nutzen aktuell: ${currentPlanName}`;
      case 'hasLandingPages':
        return `Landing Page Builder ist ab dem Pro Plan verfügbar. Sie nutzen aktuell: ${currentPlanName}`;
      case 'hasWalletIntegration':
        return `Wallet-Integration ist ab dem Pro Plan verfügbar. Sie nutzen aktuell: ${currentPlanName}`;
      case 'hasCustomBranding':
        return `Custom Branding ist nur im Enterprise Plan verfügbar. Sie nutzen aktuell: ${currentPlanName}`;
      case 'hasPrioritySupport':
        return `Priority Support ist nur im Enterprise Plan verfügbar. Sie nutzen aktuell: ${currentPlanName}`;
      default:
        return `Diese Funktion ist in Ihrem aktuellen Plan (${currentPlanName}) nicht verfügbar.`;
    }
  };

  return {
    currentFeatures,
    subscriptionStatus,
    isFeatureAvailable,
    canAddCustomers,
    canAddStamps,
    canAddReferrals,
    getRemainingCustomers,
    getRemainingStamps,
    getRemainingReferrals,
    getUpgradeMessage,
    isSubscribed: subscriptionStatus.subscribed,
    isLoading: subscriptionStatus.loading,
    currentPlan: getCurrentPlan(),
    plans,
  };
}